import { useScopeNode } from '../hooks/useScopeNode';
import { type ScopeLens, ROOT_NODE } from '../lib/hierarchy-route';
import { Button } from './Button';
import { Overview } from '../pages/Overview';
import { PhaseView } from '../pages/PhaseView';

/**
 * The synthetic "Cox Automotive" root scope at the bare lens routes (`/`,
 * `/discovery`, `/delivery`). Mirrors {@link ScopeRoute}'s loading/auth/error
 * handling — it only needs the cached `teams` tree, not a path `number` — then
 * renders the same Overview/PhaseView pages with {@link ROOT_NODE}, whose children
 * are all tier-1 portfolios (see `resolveScope`).
 */
export function RootScopeRoute({ lens }: { lens: ScopeLens }) {
  const { teams, isLoading, needsAuth, error, refetch } = useScopeNode(undefined);

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 'var(--genai-space-lg)' }} data-testid="scope-loading">
        <p className="text-secondary">Loading…</p>
      </div>
    );
  }

  if (needsAuth) {
    return (
      <div className="card mt-md" style={{ padding: 'var(--genai-space-xl)', textAlign: 'center' }} data-testid="scope-auth-required">
        <p className="text-lg font-semibold text-primary">Please sign in to view data</p>
        <p className="text-secondary mt-sm">Your session has expired. Use the Sign in button in the top-right corner to re-authenticate.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--genai-space-lg)', borderColor: 'var(--genai-sem-error)' }} data-testid="scope-error">
        <p className="text-lg font-semibold" style={{ color: 'var(--genai-sem-error)' }}>Failed to load</p>
        <p className="text-secondary mt-sm">{error}</p>
        <Button variant="secondary" className="mt-md" onClick={refetch}>Retry</Button>
      </div>
    );
  }

  return lens === 'overview'
    ? <Overview node={ROOT_NODE} teams={teams} />
    : <PhaseView node={ROOT_NODE} teams={teams} />;
}
